import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './DataPreview.css';
import LoadingScreen from '../LoadingScreen/LoadingScreen';
import excelImg from '../../assets/images/integrations/excel.png';
import quickbooksImg from '../../assets/images/integrations/quickbooks.png';

const DataPreview = ({ userId, workspaceId, onClose, schemas, setSchemas }) => {
  // State is stored as indices, defaulting to the first schema and first table.
  const [activeSchemaIndex, setActiveSchemaIndex] = useState(null);
  const [activeTableIndex, setActiveTableIndex] = useState(null);

  // Overall loading (for data sources)
  const [loading, setLoading] = useState(true);

  // Loading state for tables/columns in the preview table area
  const [tableLoading, setTableLoading] = useState(false);

  const tableRef = useRef(null);

  // Fetch the list of data sources (schemas)
  useEffect(() => {
    const fetchDataSources = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/users/get-data-sources/${userId}/${workspaceId}`,
          { withCredentials: true }
        );

        // Transform the response data into the expected format
        const transformedSchemas = res.data
          .filter(dataSource => dataSource.status === 'ready')
          .map((dataSource) => ({
            id: dataSource.id,
            type: dataSource.dataSourceType,
            name: dataSource.name,
            databaseName: dataSource.databaseName,
            schemaName: dataSource.schemaName,
            tables: [], // initialize empty array so we can fill later
          }));

        setSchemas(transformedSchemas);

        // Set defaults to the first schema/table
        if (transformedSchemas.length > 0) {
          setActiveSchemaIndex(0);
          setActiveTableIndex(0);
        }
      } catch (err) {
        console.error('Failed to fetch data sources:', err);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if we don't already have schemas in state
    if (!schemas || schemas.length === 0) {
      fetchDataSources();
    } else {
      // If schemas were already provided, just set them to the first
      setActiveSchemaIndex(0);
      setActiveTableIndex(0);
      setLoading(false);
    }
  }, []);

  // Fetch the tables for the active schema
  useEffect(() => {
    const fetchTablesFromSchema = async ({ databaseName, schemaName }) => {
      try {
        setTableLoading(true); // start table loading
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/data-sources/get-tables-from-schema/${databaseName}/${schemaName}`,
          { withCredentials: true }
        );

        const tables = res.data.map((table) => ({
          tableName: table,
          columns: [], // initialize columns array
        }));

        setSchemas((prevSchemas) => {
          if (
            activeSchemaIndex !== null &&
            activeSchemaIndex < prevSchemas.length
          ) {
            const newSchemas = [...prevSchemas];
            newSchemas[activeSchemaIndex].tables = tables;
            return newSchemas;
          }
          return prevSchemas;
        });
        setActiveTableIndex(0);
      } catch (err) {
        console.error('Failed to fetch tables:', err);
      } finally {
        setTableLoading(false); // end table loading
      }
    };

    if (
      activeSchemaIndex !== null &&
      schemas[activeSchemaIndex] &&
      (!schemas[activeSchemaIndex].tables ||
        schemas[activeSchemaIndex].tables.length === 0)
    ) {
      const { databaseName, schemaName } = schemas[activeSchemaIndex];
      fetchTablesFromSchema({ databaseName, schemaName });
    }
  }, [activeSchemaIndex, setSchemas, schemas]);

  // Fetch the columns (data preview) for the active table
  useEffect(() => {
    const fetchColumnsFromTable = async ({
      databaseName,
      schemaName,
      tableName,
    }) => {
      try {
        setTableLoading(true); // start table loading
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/data-sources/get-table-preview/${databaseName}/${schemaName}/${tableName}`,
          { withCredentials: true }
        );

        // Filter out columns that include "AIRBYTE" in their name
        const columns = res.data.filter(col => !col.columnName.includes('AIRBYTE'));

        setSchemas((prevSchemas) => {
          if (
            activeSchemaIndex !== null &&
            activeSchemaIndex < prevSchemas.length &&
            activeTableIndex !== null &&
            prevSchemas[activeSchemaIndex].tables &&
            activeTableIndex < prevSchemas[activeSchemaIndex].tables.length
          ) {
            const newSchemas = [...prevSchemas];
            newSchemas[activeSchemaIndex].tables[
              activeTableIndex
            ].columns = columns;
            return newSchemas;
          }
          return prevSchemas;
        });
      } catch (err) {
        console.error('Failed to fetch column data:', err);
      } finally {
        setTableLoading(false); // end table loading
      }
    };

    const schema = schemas[activeSchemaIndex];
    if (!schema || !schema.tables) return;

    const table = schema.tables[activeTableIndex];
    if (!table) return;

    // If columns haven't been fetched yet, or if they're empty, fetch them
    if (!table.columns || table.columns.length === 0) {
      const { databaseName, schemaName } = schema;
      const { tableName } = table;
      fetchColumnsFromTable({ databaseName, schemaName, tableName });
    }
  }, [activeTableIndex, activeSchemaIndex, setSchemas, schemas]);

  // Make table columns resizable
  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    const handleMouseDown = (e, th) => {
      const startX = e.pageX;
      const startWidth = th.offsetWidth;

      const onMouseMove = (e) => {
        const newWidth = startWidth + (e.pageX - startX);
        th.style.width = `${newWidth}px`;
      };

      const onMouseUp = () => {
        document.documentElement.removeEventListener('mousemove', onMouseMove);
        document.documentElement.removeEventListener('mouseup', onMouseUp);
      };

      document.documentElement.addEventListener('mousemove', onMouseMove);
      document.documentElement.addEventListener('mouseup', onMouseUp);
    };

    const thElements = table.querySelectorAll('th');
    thElements.forEach((th) => {
      th.addEventListener('mousedown', (e) => handleMouseDown(e, th));
    });

    return () => {
      thElements.forEach((th) => {
        th.removeEventListener('mousedown', (e) => handleMouseDown(e, th));
      });
    };
  }, [activeTableIndex, schemas, activeSchemaIndex]);

  // Handler: when switching schemas, also reset table index to 0
  const handleSchemaClick = (index) => {
    setActiveSchemaIndex(index);
    setActiveTableIndex(0);
  };

  const getLogo = (type) => {
    if (type === 'Excel') {
      return excelImg;
    } else if (type === 'QuickBooks') {
      return quickbooksImg;
    }
  };


  return (
    <div className="data-preview-container" onClick={() => onClose(false)}>
      <div className="data-preview" onClick={(e) => e.stopPropagation()}>
        {/* Overall Loading for data sources */}
        {loading ? (
          <div className="loading-screen-container">
            <LoadingScreen />
          </div>
        ) : (
          <>
            {/* Tab Navigation for each data source / schema */}
            <div className="preview-tabs">
              {schemas.map((schema, index) => (
                <div
                  key={schema.id}
                  className={`tab ${
                    index === activeSchemaIndex ? 'active' : ''
                  }`}
                  onClick={() => handleSchemaClick(index)}
                >
                  <img src={getLogo(schema.type)} alt="Excel" />
                  <h2>{schema.name}</h2>
                </div>
              ))}
            </div>

            <div className="preview-content">
              {/* Left side: list of tables */}
              <div className="tables">
                <h2>Tables</h2>
                <div className="table-list">
                  {schemas[activeSchemaIndex]?.tables?.map((table, index) => (
                    <div
                      key={table.tableName}
                      className={`table-name ${
                        index === activeTableIndex ? 'active' : ''
                      }`}
                      onClick={() => setActiveTableIndex(index)}
                    >
                      <h3>{table.tableName}</h3>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right side: data preview */}
              <div className="data">
                <h2>Sample Data (8 rows)</h2>

                {/* Table-level loading spinner */}
                {tableLoading ? (
                  <div className="table-loading-container">
                    <LoadingScreen />
                  </div>
                ) : (
                  // If we do have columns to show...
                  schemas[activeSchemaIndex]?.tables?.[activeTableIndex]
                    ?.columns?.length > 0 ? (
                    <div className="table-container">
                      <table className="table" ref={tableRef}>
                        <thead>
                          <tr>
                            {schemas[activeSchemaIndex]?.tables[
                              activeTableIndex
                            ]?.columns.map((col) => (
                              <th key={col.columnName}>{col.columnName}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {schemas[activeSchemaIndex]?.tables[
                            activeTableIndex
                          ]?.columns[0]?.data.map((_, rowIdx) => (
                            <tr key={rowIdx}>
                              {schemas[activeSchemaIndex]?.tables[
                                activeTableIndex
                              ]?.columns.map((col) => (
                                <td key={col.columnName}>{JSON.stringify(col.data[rowIdx])}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="no-data">
                      <p>No data to show.</p>
                    </div>
                  )
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DataPreview;